import React, { useMemo, useRef, useState } from 'react';
import {
  ArrowRight,
  ArrowUp,
  Filter,
  GripVertical,
  ListFilter,
  Search,
} from 'lucide-react';
import { Action, CardData } from '../types';

interface MatrizViewProps {
  actions: Action[];
  onUpdateAction: (
    id: string,
    data: { effort?: number; impact?: number }
  ) => void;
  onReorderActions?: (draggedId: string, targetId: string) => void;
  cards?: CardData[];
  zoom?: number;
}

type QuadrantInfo = {
  code: 'Q1' | 'Q2' | 'Q3' | 'Q4';
  label: string;
  tone: string;
};

const clampPercent = (value: number) => Math.min(100, Math.max(0, value));

const getQuadrant = (effort: number, impact: number): QuadrantInfo => {
  if (impact >= 50 && effort < 50) {
    return { code: 'Q1', label: 'Quick Wins', tone: 'bg-emerald-400/15 text-emerald-200 border-emerald-300/35' };
  }
  if (impact >= 50 && effort >= 50) {
    return { code: 'Q2', label: 'Estrategicos', tone: 'bg-cyan-400/15 text-cyan-200 border-cyan-300/35' };
  }
  if (impact < 50 && effort < 50) {
    return { code: 'Q3', label: 'Taticos', tone: 'bg-slate-300/10 text-slate-200 border-slate-300/25' };
  }
  return { code: 'Q4', label: 'Inviaveis', tone: 'bg-rose-400/15 text-rose-200 border-rose-300/35' };
};

export const MatrizView: React.FC<MatrizViewProps> = ({
  actions,
  onUpdateAction,
  onReorderActions,
  cards = [],
  zoom = 1,
}) => {
  const matrixRef = useRef<HTMLDivElement>(null);
  const cardHeightScale = 0.6 + (zoom - 0.5) * 0.8;

  const [filter, setFilter] = useState('');
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [draggedId, setDraggedId] = useState<string | null>(null);

  const actionNumberMap = useMemo(() => {
    const map = new Map<string, number>();
    actions.forEach((action, index) => map.set(action.id, index + 1));
    return map;
  }, [actions]);

  const normalizedFilter = filter.trim().toLowerCase();
  const filteredActions = useMemo(() => {
    if (!normalizedFilter) return actions;
    return actions.filter((action) => {
      const sequential = actionNumberMap.get(action.id);
      return (
        action.title.toLowerCase().includes(normalizedFilter) ||
        String(sequential ?? '').includes(normalizedFilter)
      );
    });
  }, [actions, normalizedFilter, actionNumberMap]);

  const active = filteredActions.filter((action) => action.status !== 'canceled');
  const backlogActive = active.filter((action) => action.effort == null || action.impact == null);
  const prioritized = active.filter((action) => action.effort != null && action.impact != null);

  const displayId = (action: Action) => {
    const sequential = actionNumberMap.get(action.id);
    return sequential ? String(sequential) : action.id.slice(-3);
  };

  const cardMap = useMemo(() => new Map(cards.map((card) => [card.id, card])), [cards]);

  const resolveDraggedActionId = (e: React.DragEvent): string | null => {
    return (
      e.dataTransfer.getData('application/x-workflow-action-id') ||
      e.dataTransfer.getData('text/plain') ||
      e.dataTransfer.getData('actionId') ||
      draggedId ||
      null
    );
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const id = resolveDraggedActionId(e);
    if (!id) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const native = e.nativeEvent as unknown as {
      offsetX?: number;
      offsetY?: number;
    };

    const hasClientCoords = !(e.clientX === 0 && e.clientY === 0);
    const pointerX = hasClientCoords
      ? e.clientX
      : rect.left + (typeof native.offsetX === 'number' ? native.offsetX : 0);
    const pointerY = hasClientCoords
      ? e.clientY
      : rect.top + (typeof native.offsetY === 'number' ? native.offsetY : 0);

    const xPercent = ((pointerX - rect.left) / rect.width) * 100;
    const yPercent = (1 - (pointerY - rect.top) / rect.height) * 100;

    // placed=true eh representado por effort/impact preenchidos.
    onUpdateAction(id, {
      effort: clampPercent(Math.round(xPercent)),
      impact: clampPercent(Math.round(yPercent)),
    });

    setDraggedId(null);
  };

  const handleDropBacklogContainer = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const id = resolveDraggedActionId(e);
    if (!id) return;

    onUpdateAction(id, { effort: undefined, impact: undefined });
    setDraggedId(null);
  };

  const handleDragStart = (e: React.DragEvent, id: string) => {
    e.dataTransfer.setData('application/x-workflow-action-id', id);
    e.dataTransfer.setData('text/plain', id);
    e.dataTransfer.setData('actionId', id);
    e.dataTransfer.effectAllowed = 'move';
    setDraggedId(id);
  };

  const handleDropOnBacklogItem = (e: React.DragEvent<HTMLDivElement>, targetId: string) => {
    e.preventDefault();
    e.stopPropagation();

    const sourceId = resolveDraggedActionId(e);
    if (!sourceId || sourceId === targetId) return;

    const sourceAction = active.find((action) => action.id === sourceId);
    const sourceIsPrioritized = Boolean(sourceAction && sourceAction.effort != null && sourceAction.impact != null);

    if (sourceIsPrioritized) {
      onUpdateAction(sourceId, { effort: undefined, impact: undefined });
      return;
    }

    if (onReorderActions) {
      onReorderActions(sourceId, targetId);
    }

    setDraggedId(null);
  };

  return (
    <div className="flex h-full w-full overflow-hidden bg-[#E2E8F0] p-4 pb-6 box-border dark:bg-[var(--bg-workspace)]">
      <div className="flex h-full min-h-0 w-full flex-row gap-4">
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden rounded border border-slate-300 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
          <div className="h-10 shrink-0 border-b border-slate-200 bg-slate-50 px-3 dark:border-slate-700 dark:bg-slate-800">
            <div className="flex h-full items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="rounded bg-slate-200 p-1 text-slate-700 dark:bg-slate-700 dark:text-slate-300">
                  <Filter size={12} />
                </div>
                <span className="text-[11px] font-bold uppercase tracking-wide text-slate-700 dark:text-slate-200">
                  Matriz de Priorizacao
                </span>
              </div>

              <div className="relative group">
                <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                <input
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder="Filtrar pontos..."
                  className="h-6 w-32 rounded-sm border border-slate-200 bg-white py-1 pl-7 pr-2 text-[10px] text-slate-900 transition-all outline-none placeholder:text-slate-400 focus:w-48 focus:border-cyan-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                />
              </div>
            </div>
          </div>

          <div className="flex min-h-0 flex-1 flex-col bg-[#F1F5F9] px-3 pb-5 pt-3 dark:bg-[var(--bg-workspace)]">
            <div className="relative flex min-h-0 flex-1 flex-row">
              <div className="mr-1 flex h-full w-8 shrink-0 flex-col items-center justify-center">
                <div className="flex h-full flex-col items-center justify-center gap-2">
                  <ArrowUp size={14} className="shrink-0 text-slate-500 dark:text-slate-400" />
                  <div className="relative flex h-20 w-4 items-center justify-center">
                    <span className="absolute -rotate-90 whitespace-nowrap text-[9px] font-black uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
                      Impacto
                    </span>
                  </div>
                </div>
              </div>

              <div className="relative flex-1 overflow-hidden rounded-sm border border-slate-300 bg-[#0f172a] shadow-sm dark:border-slate-500">
                <div
                  ref={matrixRef}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleDrop}
                  className="absolute inset-0 cursor-crosshair"
                  style={{
                    backgroundImage:
                      'linear-gradient(to right, rgba(255, 255, 255, 0.03) 1px, transparent 1px), linear-gradient(to bottom, rgba(255, 255, 255, 0.03) 1px, transparent 1px)',
                    backgroundSize: '30px 30px',
                  }}
                >
                  <div className="absolute inset-x-0 top-1/2 h-px bg-white/25" />
                  <div className="absolute inset-y-0 left-1/2 w-px bg-white/25" />

                  <div className="pointer-events-none absolute left-0 top-0 h-1/2 w-1/2 border border-white/10 bg-[rgba(255,255,255,0.01)]" />
                  <div className="pointer-events-none absolute right-0 top-0 h-1/2 w-1/2 border border-white/10 bg-[rgba(255,255,255,0.01)]" />
                  <div className="pointer-events-none absolute bottom-0 left-0 h-1/2 w-1/2 border border-white/10 bg-[rgba(255,255,255,0.01)]" />
                  <div className="pointer-events-none absolute bottom-0 right-0 h-1/2 w-1/2 border border-white/10 bg-[rgba(255,255,255,0.01)]" />

                  {[
                    { label: 'Q1 Quick Wins', position: 'top-2 left-2' },
                    { label: 'Q2 Estrategicos', position: 'top-2 right-2' },
                    { label: 'Q3 Taticos', position: 'bottom-2 left-2' },
                    { label: 'Q4 Inviaveis', position: 'bottom-2 right-2' },
                  ].map((quadrant) => (
                    <span
                      key={quadrant.label}
                      className={`pointer-events-none absolute ${quadrant.position} rounded border border-white/10 bg-white/[0.03] px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-slate-300`}
                    >
                      {quadrant.label}
                    </span>
                  ))}

                  {prioritized.map((action) => {
                    const effort = clampPercent(action.effort ?? 0);
                    const impact = clampPercent(action.impact ?? 0);

                    return (
                      <div
                        key={action.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, action.id)}
                        onMouseEnter={() => setHoveredId(action.id)}
                        onMouseLeave={() => setHoveredId(null)}
                        className="group absolute z-10 -translate-x-1/2 translate-y-1/2 cursor-move"
                        style={{
                          left: `${effort}%`,
                          bottom: `${impact}%`,
                          transition: draggedId === action.id ? undefined : 'left 140ms ease, bottom 140ms ease',
                        }}
                      >
                        <div
                          className="flex h-7 w-7 items-center justify-center rounded-full border border-emerald-200/60 text-[9px] font-black text-black animate-[pulse_2.2s_ease-in-out_infinite]"
                          style={{
                            backgroundColor: '#11caa0',
                            boxShadow: '0 0 0 1px rgba(17,202,160,0.55), 0 0 14px rgba(17,202,160,0.45), 0 0 28px rgba(17,202,160,0.22)',
                          }}
                        >
                          #{displayId(action)}
                        </div>

                        {hoveredId === action.id && (
                          <div className="pointer-events-none absolute bottom-full left-1/2 z-50 mb-1 w-44 -translate-x-1/2 rounded border border-slate-700 bg-slate-900 p-2 text-white shadow-xl">
                            <div className="mb-1 flex items-center justify-between border-b border-slate-700 pb-1">
                              <span className="text-[10px] font-bold">#{displayId(action)}</span>
                              <span className="ml-2 truncate text-[8px] uppercase text-slate-400">
                                {(action.responsavel ?? action.responsible ?? 'Sem responsavel').toString()}
                              </span>
                            </div>
                            <p className="line-clamp-2 text-[9px] leading-tight text-slate-200">{action.title}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="relative mt-1 flex h-8 shrink-0 items-center justify-center">
              <div className="flex items-center justify-center gap-2">
                <span className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">Esforco</span>
                <ArrowRight size={14} className="text-slate-500 dark:text-slate-400" />
              </div>
            </div>
          </div>
        </div>

        <div
          className="w-[300px] shrink-0 overflow-hidden rounded border border-slate-300 bg-[#F8FAFC] shadow-sm dark:border-slate-700 dark:bg-slate-900"
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDropBacklogContainer}
        >
          <div className="h-10 shrink-0 border-b border-slate-200 bg-slate-50 px-3 dark:border-slate-700 dark:bg-slate-800">
            <div className="flex h-full items-center justify-between">
              <div className="flex items-center gap-2">
                <ListFilter size={14} className="text-slate-500 dark:text-slate-400" />
                <h3 className="text-[11px] font-bold uppercase tracking-wide text-slate-700 dark:text-slate-200">Backlog</h3>
              </div>
              <span className="rounded border border-slate-300 bg-slate-200 px-1.5 py-0.5 text-[9px] font-bold text-slate-600 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-300">
                {backlogActive.length}
              </span>
            </div>
          </div>

          <div className="flex h-full min-h-0 flex-col overflow-y-auto p-2">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300">Backlog Ativo</span>
              <span className="text-[10px] text-slate-500 dark:text-slate-400">{backlogActive.length}</span>
            </div>

            <div className="space-y-1.5">
              {backlogActive.length === 0 && (
                <div className="rounded border border-dashed border-slate-300 px-2 py-3 text-center text-[10px] text-slate-500 dark:border-slate-700 dark:text-slate-400">
                  Nenhum item pendente
                </div>
              )}

              {backlogActive.map((action) => (
                <div
                  key={action.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, action.id)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => handleDropOnBacklogItem(e, action.id)}
                  className={`relative flex cursor-grab flex-col gap-1 rounded border bg-white p-2 transition-all dark:bg-slate-900 ${
                    draggedId === action.id
                      ? 'border-dashed border-slate-400 opacity-40'
                      : 'border-slate-200 hover:border-cyan-400 dark:border-slate-700'
                  }`}
                  style={{ minHeight: `${44 * cardHeightScale}px` }}
                >
                  <div className="absolute bottom-0 left-0 top-0 w-[3px] rounded-l bg-cyan-500/70" />
                  <div className="flex items-center gap-1 pl-1">
                    <GripVertical size={10} className="shrink-0 text-slate-300" />
                    <span className="shrink-0 rounded border border-slate-200 bg-slate-100 px-1 text-[9px] font-black text-slate-600">
                      #{displayId(action)}
                    </span>
                    <p className="ml-1 flex-1 truncate text-[10px] font-medium leading-tight text-slate-700 dark:text-slate-200">
                      {action.title}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="my-3 h-px bg-slate-300/45 dark:bg-slate-700/45" />

            <div className="mb-2 flex items-center justify-between">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Itens Priorizados</span>
              <span className="text-[10px] text-slate-500 dark:text-slate-400">{prioritized.length}</span>
            </div>

            <div className="space-y-1">
              {prioritized.length === 0 && (
                <div className="rounded border border-dashed border-slate-300 px-2 py-2 text-center text-[10px] text-slate-500 dark:border-slate-700 dark:text-slate-400">
                  Sem classificacoes
                </div>
              )}

              {prioritized.map((action) => {
                const effort = clampPercent(action.effort ?? 0);
                const impact = clampPercent(action.impact ?? 0);
                const quadrant = getQuadrant(effort, impact);
                const linkedCard = action.linkedNodeId ? cardMap.get(action.linkedNodeId) : undefined;
                const compactTitle = linkedCard?.title || action.title;

                return (
                  <div
                    key={action.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, action.id)}
                    className="flex cursor-grab items-center gap-2 rounded border border-slate-300/40 bg-white/60 px-2 py-1.5 dark:border-slate-700/50 dark:bg-slate-800/45"
                  >
                    <span className="shrink-0 rounded border border-slate-300/40 bg-slate-200/70 px-1 text-[9px] font-black text-slate-700 dark:border-slate-600 dark:bg-slate-700/70 dark:text-slate-200">
                      #{displayId(action)}
                    </span>
                    <p className="flex-1 truncate text-[10px] text-slate-700 dark:text-slate-200">{compactTitle}</p>
                    <span className={`shrink-0 rounded border px-1.5 py-0.5 text-[9px] font-semibold ${quadrant.tone}`}>
                      {quadrant.code}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
